import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  KeyboardAvoidingView,
  ScrollView,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Alert,
  Modal,
  useWindowDimensions,
  type LayoutChangeEvent,
} from "react-native";
import { Trans, useTranslation } from 'react-i18next';
import { useSessionStore } from "../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../state/src/useSessionStore";
import { useApiWithLoading } from '../../../../hooks/useApiWithLoading';
import { useVisualViewportHeight } from '../../../../hooks/useVisualViewportHeight';
import { ProductType } from "../../../../view-models/productType";
import { useGlobalError }  from '../../../uiComponents/GlobalErrorProvider';
import BackIcon from "../../../../../../../assets/images/BackIcon.svg";
import HelpIcon from "../../../../../../../assets/images/HelpIcon.svg";
import CloseIcon from "../../../../../../../assets/images/CloseIcon.svg";
import  Colors  from "../../../../constants/colors";
import { postTransactionToKiosk } from "../../../../services/send.service";
import { validateConsumer } from "../../../../services/receive.service";
import { getFieldsForProductRequest } from "../../../../services/receive.service";
import { buildTransactionParametersFromStore } from "../../../../helper/transactionParameterHelper";
// Receive flow step components
// import ReceiverReferenceNumberStep from "./steps/ReceiverReferenceNumberStep";
import ReceiverReferenceNumberStep, { ReceiverReferenceNumberStepHandle } from "./steps/ReceiverReferenceNumberStep";
import ReceiveAmountStep, { ReceiveAmountStepHandle } from "./steps/ReceiveAmountStep";
import ReceiverIdentificationNumberStep, { ReceiverIdentificationNumberStepHandle} from "./steps/ReceiverIdentificationNumberStep";
import ReceiverAddressStep, { ReceiverAddressStepHandle } from "./steps/ReceiverAddressStep";
import ReceiverDOBStep, { ReceiverDOBStepHandle } from "./steps/ReceiverDOBStep";
// We are verifying senderPhone number currently
// import SenderPhoneVerifyStep from "./steps/SenderPhoneVerifyStep";
import ConfirmationStep from "../../common/steps/ConfirmationStep";
import EnterPhoneStep, { EnterPhoneStepHandle } from "../../common/steps/EnterPhoneStep";
import ReceiverIdentificationTypeStep from "./steps/ReceiverIdentificationTypeStep";
import ReceiverStateOfIdStep from "./steps/ReceiverStateOfIdStep";
import HeadToCashierStep from "./steps/HeadToCashierStep";
import AdditionalReceiverInformationStep from "./steps/AdditionalReceiverInformationStep";
import { GffpHelper } from "../../../../helper/gffpHelper";
import { FieldsForProductInfo } from "../../../../view-models/FieldsForProductInfo";
import { postReferenceNumberSearch } from "../../../../services/receive.service";
import { ReferenceNumberInfo } from "../../../../view-models/ReferenceNumberInfo";
import { buildReceiveValidationRequestFromStore } from "../../../../helper/receiveValidationHelper";
import {
  extractErrorDetailFromValidateConsumerResponse,
  formatValidateConsumerErrorMessage,
} from "../../../../helper/sendValidationHelper";
import { RequiredFields } from "../../../../view-models/RequiredFields";
import NeedHelpComponent from "../../common/NeedHelpComponent";
import CalenderContainer from "../../../../../../../assets/images/CalendarContainer-2.svg";
import { capturePosthogEvent } from "../../../../utils/analytics";
import type { WebFlowRoutingProps } from "../../../../navigation/webFlowRouting";
import { useWebFlowStepSync } from "../../../../hooks/useWebFlowStepSync";
import {
  truncatePersonNameField,
  RECEIVER_FIRST_NAME_MAX_LENGTH,
  RECEIVER_LAST_NAME_MAX_LENGTH,
  SENDER_FIRST_NAME_MAX_LENGTH,
  SENDER_LAST_NAME_MAX_LENGTH,
} from "../../../../helper/personNameValidation";

interface MainSendScreenProps {
  handleClose?: () => void;
  handleFinish?: () => void;
  webFlowRouting?: WebFlowRoutingProps;
}

/** identifications.json `key` for Mexican Electoral — skip Choose State of ID (same as send flow). */
const MEXICAN_ELECTORAL_ID_TYPE_KEY = "MexicanElectorialIdentification";
/** identifications.json `key` for Passport — skip Choose State of ID (same as Mexican Electoral). */
const PASSPORT_ID_TYPE_KEY = "Passport";

/** Single linear flow (no NEW/EXISTING variant). Reload: `/receive/:stepSlug` + persisted session store (sessionStorage) restore step index and all entered data. */
const steps = [
  { key: "receiverReferenceNumber", title: "Look up your transfer", component: ReceiverReferenceNumberStep },
  // { key: "senderPhoneVerify", title: "Set PIN", component: SenderPhoneVerifyStep },
  { key: "receiveAmount", title: "Receive Amount", component: ReceiveAmountStep },
  { key: "tellUsAboutYouself", title: "Tell us about yourself", component: ConfirmationStep },
  { key: "receiverPhone", title: "Enter phone number", component: EnterPhoneStep },
  { key: "receiverIdentificationType", title: "Identification type", component: ReceiverIdentificationTypeStep },
  { key: "receiverIdNumber", title: "Your identification number", component: ReceiverIdentificationNumberStep },
  { key: "receiverstateOfId", title: "Choose State of ID", component: ReceiverStateOfIdStep },
  { key: "receiverAddress", title: "Your Address", component: ReceiverAddressStep },
  { key: "receiverDOB", title: "Your Date of Birth", component: ReceiverDOBStep },
  { key: "additionalReceiverInformation", title: "Additional Information", component: AdditionalReceiverInformationStep },
  { key: "receiverHeadToCashier", title: "Head to cashier", component: HeadToCashierStep },
];

export const MainReceiveScreen: React.FC<MainSendScreenProps> = ({
  handleClose,
  handleFinish,
  webFlowRouting,
}) => {
  const { t } = useTranslation();
  const { executeWithLoading } = useApiWithLoading();
  const { showError } = useGlobalError();
  const [currentStep, setCurrentStep] = useState(0);

  useWebFlowStepSync(webFlowRouting, steps, currentStep, setCurrentStep);

  const [isStepValid, setIsStepValid] = useState(false);
  const [needHelp, setNeedHelp] = useState(false);
  const [isDOBConfirm, setIsDOBCOnfirm] = useState(false);
  
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const senderPhotoIdNumber = useSessionStore((s: SessionState) => s.senderPhotoIdNumber);
  const setGffpCategories = useSessionStore((s: SessionState) => (s as any).setGffpCategories);
  const setGffpFieldsByCategory = useSessionStore((s: SessionState) => (s as any).setGffpFieldsByCategory);
  const setMissingRequiredFields = useSessionStore((s: SessionState) => (s as any).setMissingRequiredFields);
  const recordStepTransition = useSessionStore((s: SessionState) => (s as any).recordStepTransition);
  // Sender profile setters to hydrate from AuthConsumer
  const setConfirmationNumber = useSessionStore((s: SessionState) => (s as any).setConfirmationNumber);
  const setReferenceNumberSearchResult = useSessionStore((s: SessionState) => (s as any).setReferenceNumberSearchResult);
  const RecieverDOBStringFromStore = useSessionStore((s: any) => (s as any).RecieverDOBString);

  const selectedIdTypeKey = useSessionStore((s: SessionState) => (s as any).selectedIdTypeKey);
  const receiverPhotoIdNumber = useSessionStore((s: SessionState) => (s as any).receiverPhotoIdNumber);
  const selectedIdPhotoIdStateName = useSessionStore((s: SessionState) => s.selectedIdPhotoIdStateName);
  const receiverAddress = useSessionStore((s: SessionState) => (s as any).receiverAddress);
  const receiverCity = useSessionStore((s: SessionState) => (s as any).receiverCity);
  const receiverZipCode = useSessionStore((s: SessionState) => (s as any).receiverZipCode);
  const receiverState = useSessionStore((s: SessionState) => (s as any).receiverState);
  const recieverDOB = useSessionStore((s: SessionState) => (s as any).recieverDOB);
 
  const StepComponent = steps[currentStep].component;
  const AnyStepComponent = StepComponent as any;
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [showRefResultModal, setShowRefResultModal] = useState(false);
  const [refResult, setRefResult] = useState<{ receiverName?: string; senderName?: string; receiveAmount?: string | number; referenceNumber?: string | number } | null>(null);
  const receiverReceiveAmount = useSessionStore((s: SessionState) => (s as any).receiverReceiveAmount);
  const setReceiveAmountValidationError = useSessionStore((s: SessionState) => (s as any).setReceiveAmountValidationError);
  const referenceNumberSearchResult = useSessionStore((s: SessionState) => (s as any).referenceNumberSearchResult);
  const ReceiverReferenceNumberStepRef = React.useRef<ReceiverReferenceNumberStepHandle | null>(null);
  const ReceiveAmountStepRef = React.useRef<ReceiveAmountStepHandle | null>(null);
  const ReceiverIdentificationNumberStepRef = React.useRef<ReceiverIdentificationNumberStepHandle | null>(null);
  const ReceiverAddressStepRef = React.useRef<ReceiverAddressStepHandle | null>(null);
  const ReceiverDOBStepRef = React.useRef<ReceiverDOBStepHandle | null>(null);
  const PhoneStepRef = React.useRef<EnterPhoneStepHandle | null>(null);

  const scrollRef = useRef<ScrollView>(null);
  /** True when DOB confirmation went straight to head-to-cashier without visiting additional receiver info (missing-fields branch). */
  const skippedAdditionalReceiverInformationBeforeHeadToCashierRef = useRef(false);
  const { height: screenHeight } = useWindowDimensions();
  const webVisualViewportHeight = useVisualViewportHeight();
  const layoutHeight =
    Platform.OS === "web" ? webVisualViewportHeight ?? screenHeight : screenHeight;
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);

  const receiveStickyKeyboardContinueKeys = useMemo(
    () =>
      new Set<string>([
        "receiverReferenceNumber",
        "receiveAmount",
        "receiverPhone",
        "receiverDOB",
        "receiverIdNumber",
      ]),
    []
  );
  const stickReceiveFooterAboveKeyboard = receiveStickyKeyboardContinueKeys.has(
    steps[currentStep]?.key ?? ""
  );
  /** Web: always follow visual viewport height so the shell shrinks with the iOS keyboard (no dead band under Continue). */
  // const effectiveLayoutHeight = layoutHeight;
  const effectiveLayoutHeight =
    Platform.OS === "web"
      ? stickReceiveFooterAboveKeyboard
        ? layoutHeight
        : screenHeight
      : layoutHeight;
  const receiveStepsWithoutFooter = useMemo(
    () =>
      new Set<string>([
        "receiverstateOfId",
        "receiverIdentificationType",
        "receiverAddress",
      ]),
    []
  );
  const hasFooterForCurrentReceiveStep = !receiveStepsWithoutFooter.has(
    steps[currentStep]?.key ?? ""
  );

  const effectiveHeaderHeight = steps[currentStep].key === "tellUsAboutYouself" ? 0 : headerHeight;
  const scrollAreaHeight =
    effectiveLayoutHeight > 0
      ? effectiveLayoutHeight -
        effectiveHeaderHeight -
        (hasFooterForCurrentReceiveStep && footerHeight > 0 ? footerHeight : 0)
      : undefined;
  const stickyStepAreaHeight =
    effectiveLayoutHeight > 0 && effectiveHeaderHeight >= 0
      ? effectiveLayoutHeight - effectiveHeaderHeight
      : undefined;

  useEffect(() => {
    // scroll to top on step change
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ y: 0, animated: false });
    });
  }, [currentStep]);
  // initialize timing for first step
  useEffect(() => {
    try { recordStepTransition?.(steps[0].key); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Callback each step can use to report validity
  const handleValidationChange = (valid: boolean) => {
    setIsStepValid(valid);
  };

  const handleReceiverDOBNavigation = async () => {
    await executeWithLoading(async () => {
      try {
        const gffpFieldsByCategoryLocal = useSessionStore.getState().gffpFieldsByCategory || {};
        const validationReq = buildReceiveValidationRequestFromStore();
        const missing = new RequiredFields().getMissingFields(gffpFieldsByCategoryLocal, validationReq);
        if (missing && missing.length > 0) {
          try { setGffpFieldsByCategory?.(gffpFieldsByCategoryLocal); } catch {}
          try { setGffpCategories?.(Object.keys(gffpFieldsByCategoryLocal || {})); } catch {}
          try { setMissingRequiredFields?.(missing as any); } catch {}
          skippedAdditionalReceiverInformationBeforeHeadToCashierRef.current = false;
          const aiIndex = steps.findIndex(s => s.key === "additionalReceiverInformation");
          if (aiIndex !== -1) {
            setCurrentStep(aiIndex);
            return;
          }
        } else {
          skippedAdditionalReceiverInformationBeforeHeadToCashierRef.current = true;
          const result = await multipleCalls();
          if (result && (result as { error?: boolean }).error) {
            return;
          }
        }
      } catch (e) {
        return;
      }

      capturePosthogEvent('Receive Flow - Receiver DOB Confirmation Step', {
        'data-mg-receiver-dob': recieverDOB
      });
      const targetIndex = steps.findIndex(s => s.key === "receiverHeadToCashier");
      if (targetIndex !== -1) {
        try { recordStepTransition?.("receiverHeadToCashier"); } catch {}
        setCurrentStep(targetIndex);
      }
    }, t('STRKEY_PROCESSING'));
  }

  const handleNext = async (countryCode?: string) => {
    await executeWithLoading(async () => {
      // ---- normal forward navigation below ----
      if (steps[currentStep].key === "receiverReferenceNumber") {
        const isValid = await ReceiverReferenceNumberStepRef.current?.submit();
        if (!isValid) return; // stay on same step
        // Call reference number search before moving on
        const payload = await postReferenceNumberSearch({ 
          agentID: agentId,
          unitProfileID: unitProfileId,
          referenceNumber: senderPhotoIdNumber });

        try {
         const rawData: ReferenceNumberInfo = (payload && (payload as any).data !== undefined) ? (payload as any).data : payload;
         let parsed: any = rawData;
         if (typeof rawData === 'string') {
           try { parsed = JSON.parse(rawData); } catch {}
         }
         try { setReferenceNumberSearchResult?.(parsed); } catch {}
         const errDetail = parsed?.ErrorDetail || parsed?.errorDetail;
         if (errDetail) {
           const errCode = errDetail?.ErrorCode ?? errDetail?.errorCode ?? 'UNKNOWN';
           const errString = errDetail?.ErrorString ?? errDetail?.errorString ?? t("ERROR_AN_ERROR_OCCURED");
           const message = errCode + ': ' + errString;
           showError(message);
           Alert.alert(t('ERROR'), message, [{ text: t("STRKEY_OK")}]);
           return;
         }
        const receiverFirst = truncatePersonNameField(
          String(parsed?.ReceiverFirstName ?? parsed?.receiverFirstName ?? ""),
          RECEIVER_FIRST_NAME_MAX_LENGTH
        );
        const receiverLast = truncatePersonNameField(
          String(parsed?.ReceiverLastName ?? parsed?.receiverLastName ?? ""),
          RECEIVER_LAST_NAME_MAX_LENGTH
        );
        const senderFirst = truncatePersonNameField(
          String(parsed?.SenderFirstName ?? parsed?.senderFirstName ?? ""),
          SENDER_FIRST_NAME_MAX_LENGTH
        );
        const senderLast = truncatePersonNameField(
          String(parsed?.SenderLastName ?? parsed?.senderLastName ?? ""),
          SENDER_LAST_NAME_MAX_LENGTH
        );
        const receiveAmountVal = parsed?.ReceiveAmount ?? parsed?.receiveAmount ?? "";
        const referenceNumVal = parsed?.ReferenceNumber ?? parsed?.referenceNumber ?? "";
        setRefResult({
          receiverName: `${receiverFirst} ${receiverLast}`.trim(),
          senderName: `${senderFirst} ${senderLast}`.trim(),
          receiveAmount: receiveAmountVal,
          referenceNumber: referenceNumVal,
        });
     //   setShowRefResultModal(true);
      //  return;
        // TODO: Need to uncomment this below if
        /* if ((rawData?.errorDetail !== null &&
            rawData?.errorDetail?.errorCode !== undefined &&
            rawData?.errorDetail?.errorCode !== "") ||
            rawData?.TransactionStatus !== TransactionStatus.AVAIL || !rawData?.OkForAgent) {
              //showError(rawData?.errorDetail?.errorCode + ': ' + rawData?.errorDetail?.errorString);
              showError("your Transaction Refrence Number is not available. Please contact the sender");
        } */
        
        } catch (e) {
          const msg = String((e as any)?.message || e || t("ERROR_REFERENCE_NUMBER_LOOKUP_FAILED"));
          try { showError?.(msg); } catch {}
          Alert.alert(t('ERROR'), msg, [{ text: t("STRKEY_OK") }]);
        }
        capturePosthogEvent('Receive Flow - Reference Number Capture Step', {
          'data-mg-reference-number': senderPhotoIdNumber,
          'data-mg-receiver-name': refResult?.receiverName,
          'data-mg-sender-name': refResult?.senderName,
          'data-mg-receive-amount': refResult?.receiveAmount
        });
        const targetIndex = steps.findIndex(s => s.key === "receiveAmount");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "receiveAmount") {
        const isValid = await ReceiveAmountStepRef.current?.submit();
        if (!isValid) return; // stay on same step
        // Compare entered amount with lookup result
        try {
          const expected = (refResult?.receiveAmount ?? (referenceNumber ? undefined : undefined));
          const expectedNum = Number(expected);
          const enteredNum = Number(receiverReceiveAmount || "0");
          if (!isNaN(expectedNum) && !isNaN(enteredNum)) {
            if (Number(expectedNum.toFixed ? expectedNum.toFixed(2) : expectedNum) !== Number(enteredNum.toFixed ? enteredNum.toFixed(2) : enteredNum)) {
              try { setReceiveAmountValidationError?.(t("ERROR_RECEIVE_AMOUNT_DOES_NOT_MATCH")); } catch {}
              const msg = t("ERROR_RECEIVE_AMOUNT_DOES_NOT_MATCH");
              showError(msg);
              Alert.alert(t('ERROR'), msg, [{ text: t("STRKEY_OK") }]);
              return; // stay on same screen
            }
          }
          try { setReceiveAmountValidationError?.(null); } catch {}
        } catch {}
        // On match: call getFieldsForProductRequest similar to send flow
        try {
          const r = referenceNumberSearchResult || {};
          const receiveCountry =  r.OriginatingCountry;
          const receiveCurrency = r?.ReceiveCurrency;
          const amountNum = Number(String(receiverReceiveAmount || "0").replace(/,/g, ""));
          const payload: any = {
            unitProfileID: unitProfileId || "",
            agentID: agentId || "",
            receiveCountry,
            thirdPartyType: "NONE",
            receiveCurrency,
            amount: isFinite(amountNum) ? amountNum : 0,
            sendCurrency:  r.OriginalSendCurrency,
            productType: ProductType.RCV,
            CustomerId: "0",
          };
          const gffpResponse: FieldsForProductInfo = await getFieldsForProductRequest(payload);
          const requiredFields: any[] = GffpHelper.filterRequiredAndOptionalFields(gffpResponse) as any[];
          const groupedByCategory: Record<string, any[]> = requiredFields.reduce((acc: Record<string, any[]>, field: any) => {
            const category = field.FieldCategory ?? field.fieldCategory ?? 'Uncategorized';
            if (!acc[category]) acc[category] = [];
            acc[category].push(field);
            return acc;
          }, {});
          Object.keys(groupedByCategory).forEach((cat) => {
            groupedByCategory[cat].sort((a, b) => {
              const ao = a.DisplayOrder ?? a.displayOrder ?? 0;
              const bo = b.DisplayOrder ?? b.displayOrder ?? 0;
              return ao - bo;
            });
          });
          const distinctCategories = Object.keys(groupedByCategory);
          try { setGffpFieldsByCategory?.(groupedByCategory); } catch {}
          try { setGffpCategories?.(distinctCategories); } catch {}
        } catch (e: any) {
          const msg = String(e?.message ?? t("ERROR_UNABLE_TO_LOAD_REQUIRED_FIELDS"));
          try { showError?.(msg); } catch {}
          Alert.alert(t('ERROR'), msg, [{ text: t("STRKEY_OK") }]);
          return;
        }
        capturePosthogEvent('Receive Flow - Receive Amount Capture Step', {
          'data-mg-reference-number': refResult?.referenceNumber,
          'data-mg-receive-amount': refResult?.receiveAmount
        });
        const targetIndex = steps.findIndex(s => s.key === "tellUsAboutYouself");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "tellUsAboutYouself") {
        capturePosthogEvent('Receive Flow - Tell Us About Yourself Step', {});
        const targetIndex = steps.findIndex(s => s.key === "receiverPhone");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "receiverPhone") {
        const isValid = await PhoneStepRef.current?.phoneSubmit();
        if (!isValid) return; // stay on same step
        const targetIndex = steps.findIndex(s => s.key === "receiverIdentificationType");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "receiverIdentificationType") {
        capturePosthogEvent('Receive Flow - Receiver ID Type Capture Step', {
          'data-mg-receiver-id-type': selectedIdTypeKey
        });
        const targetIndex = steps.findIndex(s => s.key === "receiverIdNumber");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "receiverIdNumber") {
        const isValid = await ReceiverIdentificationNumberStepRef.current?.submit();
        if (!isValid) return; // stay on same step
        capturePosthogEvent('Receive Flow - Receiver ID Number Capture Step', {
          'data-mg-receiver-id-number': receiverPhotoIdNumber
        });
        const session = useSessionStore.getState() as any;
        const selectedTypeKeyFromStore = session.selectedIdTypeKey as string | null | undefined;
        const skipReceiverStateOfId =
          selectedTypeKeyFromStore === MEXICAN_ELECTORAL_ID_TYPE_KEY ||
          selectedTypeKeyFromStore === PASSPORT_ID_TYPE_KEY;
        const nextKeyAfterIdNumber = skipReceiverStateOfId ? "receiverAddress" : "receiverstateOfId";
        const targetIndex = steps.findIndex(s => s.key === nextKeyAfterIdNumber);
        if (targetIndex !== -1) {
          try { recordStepTransition?.(nextKeyAfterIdNumber); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "receiverstateOfId") {
        capturePosthogEvent('Receive Flow - Receiver State of ID Capture Step', {
          'data-mg-receiver-id-state': selectedIdPhotoIdStateName
        });
        const targetIndex = steps.findIndex(s => s.key === "receiverAddress");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("receiverAddress"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "receiverAddress") {
        const isValid = await ReceiverAddressStepRef.current?.submit();
        if (!isValid) return; // stay on same step
        capturePosthogEvent('Receive Flow - Receiver Address Capture Step', {
          'data-mg-receiver-address': receiverAddress,
          'data-mg-receiver-city': receiverCity,
          'data-mg-receiver-zipcode': receiverZipCode,
          'data-mg-receiver-state': receiverState
        });
        const targetIndex = steps.findIndex(s => s.key === "receiverDOB");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("receiverDOB"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "receiverDOB") {
        capturePosthogEvent('Receive Flow - Receiver DOB Capture Step', {
          'data-mg-receiver-dob': recieverDOB
        });
        const isValid = await ReceiverDOBStepRef.current?.submit();
        if (!isValid) return; // stay on same step
        setIsDOBCOnfirm(true);
      } else if (steps[currentStep].key === "receiverHeadToCashier") {
        // Finish: allow host app to handle navigation back to dashboard with kiosk token
        try { handleFinish?.(); } catch {}
        return;
      }
    }, t('STRKEY_PROCESSING'));
  };

  const multipleCalls = async (): Promise<
    { error: true; errorMessage: string } | undefined
  > => {
      try {
        const validationReq = buildReceiveValidationRequestFromStore();
        const validationResponse = await validateConsumer(validationReq);
        const errDetail = extractErrorDetailFromValidateConsumerResponse(validationResponse);
        if (errDetail) {
          const message = formatValidateConsumerErrorMessage(errDetail);
          try { showError?.(message); } catch {}
          Alert.alert(t('ERROR'), message, [{ text: t("STRKEY_OK") }]);
          return { error: true, errorMessage: message };
        }
        try {
          const ref = (validationResponse && ((validationResponse.confirmationNumber) || (validationResponse.ConfirmationNumber) || (validationResponse.referenceNumber) || (validationResponse.ReferenceNumber))) ?? undefined;
          if (ref) {
            try { setConfirmationNumber?.(String(ref)); } catch {}
          }
        } catch {}
        // Build transaction parameters and post to kiosk
        try {
          const txParams = buildTransactionParametersFromStore(validationResponse, 'receive', '','','');
          const txResp = await postTransactionToKiosk(txParams);
        } catch (e: any) {
          const msg = String(e?.message ?? t("ERROR_FAILED_TO_POST_TRANSACTION"));
          try { showError?.(msg); } catch {}
          Alert.alert(t('ERROR'), msg, [{ text: t("STRKEY_OK") }]);
          return { error: true, errorMessage: msg };
        }
      } catch (e: any) {
        const msg = String(e?.message ?? t("ERROR_VALIDATION_FAILED"));
        try { showError?.(msg); } catch {}
        Alert.alert(t('ERROR'), msg, [{ text: t("STRKEY_OK") }]);
        return { error: true, errorMessage: msg };
      }
  }

  // Enable Continue button for SendingAmountEntry step
  useEffect(() => {
    if (steps[currentStep].key === "sendingAmountEntry") {
      setIsStepValid(true);
    } else if (steps[currentStep].key !== "phone") {
      // Default allow for other non-phone steps unless they manage validation || steps[currentStep].key === "receiverIdentificationType"
      setIsStepValid(true);
    } else {
      // Phone step manages its own
      setIsStepValid(false);
    }
  }, [currentStep]);

  const handleBack = () => {
    const curKey = steps[currentStep]?.key;
    if (curKey === "receiverReferenceNumber") {
      handleClose?.();
      return;
    }
    if (currentStep <= 0) return;
    const prevIdx = currentStep - 1;
    const session = useSessionStore.getState() as any;
    const selectedTypeKeyFromStore = session.selectedIdTypeKey as string | null | undefined;
    if (
      curKey === "receiverAddress" &&
      steps[prevIdx]?.key === "receiverstateOfId" &&
      (selectedTypeKeyFromStore === MEXICAN_ELECTORAL_ID_TYPE_KEY ||
        selectedTypeKeyFromStore === PASSPORT_ID_TYPE_KEY)
    ) {
      const idNumberIdx = steps.findIndex((s) => s.key === "receiverIdNumber");
      if (idNumberIdx !== -1) {
        setCurrentStep(idNumberIdx);
        return;
      }
    }
    if (
      curKey === "receiverHeadToCashier" &&
      steps[prevIdx]?.key === "additionalReceiverInformation" &&
      skippedAdditionalReceiverInformationBeforeHeadToCashierRef.current
    ) {
      const dobIdx = steps.findIndex((s) => s.key === "receiverDOB");
      if (dobIdx !== -1) {
        try { recordStepTransition?.("receiverDOB"); } catch {}
        setCurrentStep(dobIdx);
        return;
      }
    }
    setCurrentStep((prev) => prev - 1);
  };

  const handleCustomerNotFound = () => {
    const targetIndex = steps.findIndex(s => s.key === "country");
    if (targetIndex !== -1) {
      setCurrentStep(targetIndex);
    }
  };

  const closeNeedHelp = () => {
    setNeedHelp(false);
  }

  const handleNeedHelpCall = () => {
    // Calling to provided number
  }

  
  const formatDateForPOPup = () => {
    try {
      // Always read latest from store to avoid stale closures
      const { senderDOB: dobFromState, RecieverDOBString } = useSessionStore.getState() as any;
      // Prefer Date object from store
      const asDate: any = dobFromState instanceof Date ? dobFromState : undefined;
      let date: Date | undefined = asDate && !isNaN(asDate as any) ? (asDate as Date) : undefined;

      // Fallback to string forms (senderDOB or senderDOBString)
      if (!date) {
        const rawString = (typeof dobFromState === 'string' ? dobFromState : undefined) || (RecieverDOBString as string | undefined) || (RecieverDOBStringFromStore as string | undefined);
        if (rawString) {
          const s = String(rawString).trim();
          if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
            // YYYY-MM-DD or ISO; create at noon to avoid TZ shifts
            const y = Number(s.slice(0, 4));
            const m = Number(s.slice(5, 7)) - 1;
            const d = Number(s.slice(8, 10));
            date = new Date(y, m, d, 12, 0, 0, 0);
          } else if (/^(\d{2})\/(\d{2})\/(\d{4})$/.test(s)) {
            // Handle both MM/DD/YYYY and DD/MM/YYYY
            const [, p1, p2, yyyy] = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/) as RegExpMatchArray;
            const n1 = Number(p1);
            const n2 = Number(p2);
            let mm = n1;
            let dd = n2;
            // If first part > 12, it's DD/MM/YYYY; swap
            if (n1 > 12 && n2 <= 12) {
              mm = n2;
              dd = n1;
            }
            // Create date at noon local time to avoid timezone boundary issues
            date = new Date(Number(yyyy), mm - 1, dd, 12, 0, 0, 0);
          } else {
            const tmp = new Date(s);
            if (!isNaN(tmp as any)) date = tmp;
          }
        }
      }

      if (!date || isNaN(date as any)) return '';
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return '';
    }
  };

  const renderDOBPopUp = () => (
    <View style={styles.root}>
      <TouchableOpacity
        style={styles.scrim}
        activeOpacity={1}
        onPress={() => setIsDOBCOnfirm(false)}
      />
      <View style={styles.bottomSheet}>
            {/* <View style={styles.modalHeaderRow}> */}
              <TouchableOpacity style={styles.modalHeaderRow}
              onPress={() => { setIsDOBCOnfirm(false); }}>
                <CloseIcon width={24} height={24}/>
              </TouchableOpacity>
            {/* </View> */}
            <View style={styles.modalBodyCenter}>
              <CalenderContainer width={64} height={64}/>
              <Text style={styles.modalTitleDOB}>{t("SENDER_DOB_CONFIRMATION_TITLE")}</Text>
              <Text style={styles.modalInfo}>
                <Trans
                  i18nKey="SENDER_DOB_CONFIRM_SUBTITLE"
                  components={{
                    legalId: (
                      <Text
                        style={[
                          styles.modalInfo,
                          {
                            color: Colors.black,
                            fontWeight: "700",
                            paddingHorizontal: 0,
                          },
                        ]}
                      />
                    ),
                  }}
                />
              </Text>
              <Text style={styles.modalDOB}>{formatDateForPOPup()}</Text>
              </View>
              <View style={styles.footer}>
              <TouchableOpacity
                testID="senderDOBConfirm"
                style={styles.confirmBtn}
                onPress={() => { setIsDOBCOnfirm(false); handleReceiverDOBNavigation(); }}
              >
                <Text style={[styles.cancelText, { color: Colors.white}]}>{t("CONFIRM")}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                testID="senderDOBEdit"
                style={styles.cancelBtn}
                onPress={() => setIsDOBCOnfirm(false)}
              >
                <Text style={[styles.cancelText, { lineHeight: 24 }]}>{t("EDIT")}</Text>
              </TouchableOpacity>
            </View>
      </View>
    </View>
  )


  return (
    <KeyboardAvoidingView
      style={[
        styles.container,
        Platform.OS === "web" && effectiveLayoutHeight > 0
          ? { height: effectiveLayoutHeight, minHeight: effectiveLayoutHeight, maxHeight: effectiveLayoutHeight }
          : effectiveLayoutHeight > 0 && { height: effectiveLayoutHeight },
      ]}
      behavior={
        Platform.OS === "web"
          ? undefined
          : stickReceiveFooterAboveKeyboard
            ? "padding"
            : undefined
      }
      enabled={Platform.OS === "web" ? false : stickReceiveFooterAboveKeyboard}
      keyboardVerticalOffset={Platform.OS === "ios" && stickReceiveFooterAboveKeyboard ? 60 : 0}
    >
      <View style={styles.mainWrapper}>
        {/* --- HEADER --- */}
        { (steps[currentStep].key !== "tellUsAboutYouself") && 
        <>
        <View
          style={[styles.header, Platform.OS === "web" && styles.headerFixed]}
          onLayout={(e: LayoutChangeEvent) => setHeaderHeight(e.nativeEvent.layout.height)}
        >
          {/* Top Bar */}
          <View style={styles.topBar}>
            {steps[currentStep].key !== "receiverHeadToCashier" ? (
              <TouchableOpacity style={styles.topBarButton} onPress={handleBack} testID="backButton">
                <BackIcon width={24} height={24}/>
              </TouchableOpacity>
              ) : (
                <Text></Text>
              )
            }
            <View style={styles.topBarRight}>
              <TouchableOpacity onPress={() => setNeedHelp(true)} style={styles.topBarButton} testID="helpButton">
                <HelpIcon width={24} height={24}/>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.topBarButton, { marginLeft: 10}]}
                testID="closeButton"
                onPress={handleClose}
              >
                <CloseIcon width={24} height={24}/>
              </TouchableOpacity>
            </View>
          </View>

          {/* Progress Bar */}
          <View style={styles.progressRow}>
            {steps.map((_, i) => (
              <View
                key={i}
                style={[
                  styles.progressSegment,
                  { borderTopLeftRadius: i === 0 ? 9 : 0 },
                  { borderBottomLeftRadius: i === 0 ? 9 : 0 },
                  { borderTopRightRadius: i === steps.length - 1 ? 9 : 0 },
                  { borderBottomRightRadius: i === steps.length - 1 ? 9 : 0 },
                  { backgroundColor: i <= currentStep ? Colors.titleTextColor : Colors.searchBackground },
                ]}
              />
            ))}
          </View>
        </View>
        {Platform.OS === "web" && (
          <View style={[styles.headerSpacer, headerHeight > 0 && { height: headerHeight }]} />
        )}
        </>
        }

        {/* --- STEP CONTENT --- */}
        {(steps[currentStep].key === "receiverstateOfId") ? (
          <View
            style={[
              styles.scrollView,
              { flex: 1, minHeight: 0 },
              stickyStepAreaHeight != null && stickyStepAreaHeight > 0 && { maxHeight: stickyStepAreaHeight },
            ]}
          >
            <AnyStepComponent
              onValidationChange={handleValidationChange}
              onCountrySelected={handleNext}
            />
          </View>
        ) : steps[currentStep].key === "receiverAddress" ? (
          (() => {
            const stepAreaHeight = scrollAreaHeight;
            const hasHeight = stepAreaHeight != null && stepAreaHeight > 0;
            return (
              <View
                style={[
                  styles.stepAreaContainer,
                  hasHeight && {
                    height: stepAreaHeight,
                    maxHeight: stepAreaHeight,
                  },
                  !hasHeight &&
                    Platform.OS === "web" &&
                    styles.stepAreaContainerWebFallback,
                ]}
              >
                <AnyStepComponent
                  testID="step-receiverAddress"
                  ref={ReceiverAddressStepRef}
                  onValidationChange={handleValidationChange}
                  onContinue={handleNext}
                />
              </View>
            );
          })()
        ) : (
        <ScrollView
          ref={scrollRef}
          style={[
            styles.scrollView,
            scrollAreaHeight != null && scrollAreaHeight > 0 && { maxHeight: scrollAreaHeight },
          ]}
          contentContainerStyle={steps[currentStep].key === "receiverHeadToCashier" ? styles.scrollContainerHeadToCashier : Platform.OS === "web" && {
            paddingBottom: footerHeight || 100,
          }}
          scrollEnabled={steps[currentStep].key !== "receiverIdNumber"}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          bounces={steps[currentStep].key !== "receiverIdNumber"}
        >
           {steps[currentStep].key === "receiverReferenceNumber" ? (
            <AnyStepComponent
              ref={ReceiverReferenceNumberStepRef}
              onValidationChange={handleValidationChange}
            />
          ) : steps[currentStep].key === "receiveAmount" ? (
            <AnyStepComponent
              ref={ReceiveAmountStepRef}
              onValidationChange={handleValidationChange}
            />
          ) : steps[currentStep].key === "receiverPhone" ? (
            <AnyStepComponent
              ref={PhoneStepRef}
              onValidationChange={handleValidationChange}
              isAutoFocus
            />
          ) : steps[currentStep].key === "receiverIdNumber" ? (
            <AnyStepComponent
              ref={ReceiverIdentificationNumberStepRef}
              onValidationChange={handleValidationChange}
            />
          ) : steps[currentStep].key === "receiverDOB" ? (
            <AnyStepComponent
              ref={ReceiverDOBStepRef}
              onValidationChange={handleValidationChange}
            />
          ) : <AnyStepComponent
              onValidationChange={handleValidationChange}
              onReferenceNumberChange={setReferenceNumber}
              onCountrySelected={handleNext}
          />}
        </ScrollView>
        )}
        <Modal
          visible={needHelp}
          transparent
          animationType="none"
          // onRequestClose={() => setShowPopup(false)}
          onRequestClose={() => closeNeedHelp?.()}
        >
          <NeedHelpComponent
            closeNeedHelp={closeNeedHelp}
            handleNeedHelpCall={handleNeedHelpCall}
          />
        </Modal>
        <Modal
          visible={isDOBConfirm}
          transparent
          animationType="none"
          onRequestClose={() => setIsDOBCOnfirm(false)}
        >
          {renderDOBPopUp()}
        </Modal>
        {/* Modal for reference number result */}
        <Modal
          visible={showRefResultModal}
          transparent
          animationType="none"
          onRequestClose={() => setShowRefResultModal(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContainer}>
              <Text style={styles.modalTitle}>{t("TRANSACTION_DETAILS")}</Text>
              <View style={styles.modalDivider} />
              <View style={{ marginTop: 12 }}>
                <Text style={styles.modalRowLabel}>{t("TRANSACTION_RECEIVER_NAME")}</Text>
                <Text style={styles.modalRowValue}>{refResult?.receiverName || '-'}</Text>
                <Text style={[styles.modalRowLabel, { marginTop: 8 }]}>{t("TRANSACTION_SENDER_NAME")}</Text>
                <Text style={styles.modalRowValue}>{refResult?.senderName || '-'}</Text>
                <Text style={[styles.modalRowLabel, { marginTop: 8 }]}>{t("RECEIVE_AMOUNT")}</Text>
                <Text style={styles.modalRowValue}>{String(refResult?.receiveAmount ?? '-')}</Text>
                <Text style={[styles.modalRowLabel, { marginTop: 8 }]}>{t("REFERENCE_NUMBER")}</Text>
                <Text style={styles.modalRowValue}>{String(refResult?.referenceNumber ?? '-')}</Text>
              </View>
              <TouchableOpacity style={[styles.button, { marginTop: 16 }]} onPress={() => setShowRefResultModal(false)} testID="okButton">
                <Text style={styles.buttonText}>{t("STRKEY_OK")}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>
        {/* --- FIXED FOOTER --- */}
        {hasFooterForCurrentReceiveStep && (
        <View
          style={[
            styles.footerContainer,
            styles.footerShrink,
            Platform.OS === "web" &&
              !stickReceiveFooterAboveKeyboard &&
              styles.footerContainerWebViewportPinned,
          ]}
          onLayout={(e: LayoutChangeEvent) => setFooterHeight(e.nativeEvent.layout.height)}
        >
          <TouchableOpacity testID="continueButton"
            style={[
              styles.button,
              { backgroundColor: Colors.secondaryText },
            ]}
            onPress={() => handleNext()}
            // disabled={!isStepValid}
          >
            <Text style={styles.buttonText}>
              {currentStep === steps.length - 1 ? t("I_AM_FINISHED") : t("STRKEY_CONTINUE")}
            </Text>
          </TouchableOpacity>
        </View>
        )}
      </View>
    </KeyboardAvoidingView>
);
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    backgroundColor: Colors.white,
    ...(Platform.OS === 'web' && {
      // dvh tracks mobile browser chrome better than vh (iOS Safari URL bar)
      minHeight: '100dvh' as any,
      height: '100dvh' as any,
      maxHeight: '100dvh' as any,
      overflow: 'hidden' as any,
    })
  },
  header: {
    paddingHorizontal: 24,
    flexShrink: 0,
  },
  headerFixed: {
    ...(Platform.OS === "web" && {
      position: "fixed" as any,
      top: 0,
      left: 0,
      right: 0,
      zIndex: 10,
      backgroundColor: Colors.white,
    }),
  },
  headerSpacer: {
    ...(Platform.OS === "web" && {
      height: 80,
      width: "100%",
    }),
  },
  footerShrink: {
    flexShrink: 0,
  },
  dropdownPriorityScrollLayer: {
    ...(Platform.OS === "web" && {
      position: "relative" as any,
      zIndex: 5,
      overflow: "visible" as any,
    }),
  },
  footerBehindDropdownLayer: {
    ...(Platform.OS === "web" && {
      position: "relative" as any,
      zIndex: 1,
    }),
  },
  content: {
    flex: 1,
  },
  mainWrapper: {
    flex: 1,
    flexDirection: "column",
    ...(Platform.OS === 'web' && {
      minHeight: 0 as any,
      overflow: 'hidden' as any,
    }),
  },
  scrollView: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      minHeight: 0 as any,
      overflow: 'auto' as any,
      overflowX: 'hidden' as any,
    })
  },
  /** Receive address: step owns scroll + inline Continue (same idea as send `receiverName`). */
  stepAreaContainer: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    ...(Platform.OS === "web" && {
      overflow: "hidden" as any,
      overflowX: "hidden" as any,
    }),
  },
  stepAreaContainerWebFallback: {
    height: "100%" as any,
    minHeight: "50vh" as any,
  },
  scrollContainer: {
    paddingBottom: 24,
  },
  scrollContainerHeadToCashier: {
    paddingBottom: 32,
  },
  footerContainer: {
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
    paddingTop: 14,
    /** 24px under Continue; web adds safe-area so inset-only does not replace the 24px. */
    ...(Platform.OS === "web"
      ? ({
          paddingBottom: "calc(24px + env(safe-area-inset-bottom, 0px))",
        } as any)
      : { paddingBottom: 24 }),
  },
  footerContainerWebViewportPinned: {
    position: "fixed" as any,
    left: 0,
    right: 0,
    width: "100%",
    zIndex: 999,
    bottom: 0,
  },
  button: {
    backgroundColor: Colors.titleTextColor,
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: "center",
    marginTop: 14,
    marginBottom: 0,
  },
  topBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 12
  },
  topBarButton: {
    padding: 1
  },
  topBarRight: {
    flexDirection: "row"
  },
  IconStyle: {
    height: 24,
    width: 24
  },
  iconText: {
    fontSize: 20,
    color: "#111"
  },
  progressRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
    height: 6
  },
  progressSegment: {
    flex: 1,
    height: 4,
  },
  title: {
    fontSize: 20,
    fontWeight: "700",
    marginBottom: 16
  },
  buttonText: {
    color: Colors.buttonTextColor,
    fontSize: 16,
    fontWeight: "500",
    fontFamily: 'Rubik',
    lineHeight: 20
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)"
  },
  root: {
    flex: 1,
    justifyContent: "flex-end",
    position: "fixed" as any, // web only
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  scrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.4)",
  },

  modalContent: {
    backgroundColor: Colors.white,
    padding: 24,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    width: "100%",
    zIndex: 2, // ensures it stays above scrim
  },
  modalContainer: {
    width: "88%",
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 16
  },
  modalTitle: {
    fontFamily: 'Rubik',
    fontSize: 24,
    fontWeight: "500",
    color: Colors.accountLock,
    lineHeight: 28,
    marginVertical: 26
  },
  modalRowLabel: {
    fontSize: 14,
    color: "#555"
  },
  modalRowValue: {
    fontSize: 16,
    fontWeight: "600",
    color: "#111"
  },
  modalDivider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginTop: 8
  },
  bottomSheet: {
    backgroundColor: Colors.white,
    padding: 24,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  modalHeaderRow: {
    alignSelf: 'flex-end',
    marginBottom: 48
  },
  modalBodyCenter: {
    alignItems: 'center',
    alignSelf: 'stretch',
    width: '100%',
  },
  modalTitleDOB: {
    fontFamily: 'Rubik',
    fontSize: 24,
    fontWeight: "500",
    color: Colors.accountLock,
    lineHeight: 28,
    marginVertical: 26,
    textAlign: 'center',
    alignSelf: 'stretch',
    width: '100%',
  },
  modalInfo: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: "400",
    color: Colors.accountLock,
    lineHeight: 18,
    marginBottom: 26,
    textAlign: 'center',
    paddingHorizontal: 10
  },
  modalDOB: {
    fontFamily: 'Rubik',
    fontSize: 32,
    fontWeight: "500",
    color: Colors.accountLock,
    lineHeight: 38,
    marginBottom: 48,
    textAlign: 'center',
    alignSelf: 'stretch',
    width: '100%',
  },
  confirmBtn: {
    backgroundColor: Colors.titleTextColor,
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
    height: 48
  },
  footer: {
    // paddingHorizontal: 24,
    paddingBottom: 24,
    backgroundColor: Colors.white
  },
  cancelBtn: { paddingTop: 24, alignItems: "center" },
  cancelText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    color: Colors.darkGrey,
    lineHeight: 20,
  },
});